/**
 * The text check between the screen and the API (UC-2, slice 06). The chips speak the
 * workspace's fields, the API its own (a date of birth is `dob`, a name hides its variants and
 * the name as printed). A search that did not run — an error, a timeout, a missing or
 * populating index, an audit that could not be written — is *not checked*, never "no subject
 * found": only an `ok` answer with no results is an empty result.
 */
import {
  TEXT_CHECK_FIELDS,
  type TextCheckField,
  type TextCheckHit,
  type TextCheckMatchField,
  type TextCheckQuery,
  type TextCheckResult,
  type TextMatchKind,
} from '@/components/humint/text-check/text-check-types'
import {
  ENCOUNTER_TYPES,
  type EncounterType,
  type NotCheckedReason,
} from '@/components/humint/types'
import { ApiError, UploadTimeoutError } from '@/services/api/client'
import type {
  TextCheckFieldDto,
  TextCheckRequest,
  TextCheckResponse,
  TextCheckResultDto,
  TextMatchKindDto,
} from '@/services/api/humint'
import { humintErrorCode, isExtensionOff, isNoAnswer, isNotMember } from './errors'

/** The API fields behind each chip. */
const CHIP_FIELDS: Readonly<Record<TextCheckField, readonly TextCheckFieldDto[]>> = {
  name: ['name', 'name_variant', 'name_native'],
  nickname: ['nickname'],
  date_of_birth: ['dob'],
  document_number: ['document_number', 'national_id'],
  service_number: ['service_number'],
  capture_tag: ['capture_tag'],
}

const MATCH_FIELD: Readonly<Record<TextCheckFieldDto, TextCheckMatchField>> = {
  name: 'name',
  name_variant: 'name_variant',
  nickname: 'nickname',
  name_native: 'name_native',
  dob: 'date_of_birth',
  document_number: 'document_number',
  service_number: 'service_number',
  national_id: 'national_id',
  capture_tag: 'capture_tag',
}

/** A prefix match on an identifier reads "partial match". */
const MATCH_KIND: Readonly<Record<TextMatchKindDto, TextMatchKind>> = {
  exact: 'exact',
  prefix: 'partial',
  transliteration: 'transliteration',
  fuzzy: 'fuzzy',
}

/** Every chip on: search every field. */
export const DEFAULT_TEXT_CHECK_QUERY: TextCheckQuery = {
  text: '',
  fields: [...TEXT_CHECK_FIELDS],
}

/** The request body, or null when nothing is typed. Every chip on sends `fields: null`. */
export function toTextCheckRequest(query: TextCheckQuery): TextCheckRequest | null {
  const text = query.text.trim()
  if (!text) return null
  const all = TEXT_CHECK_FIELDS.every(field => query.fields.includes(field))
  return { text, fields: all ? null : query.fields.flatMap(field => CHIP_FIELDS[field]) }
}

export const isEncounterType = (type: string): type is EncounterType =>
  ENCOUNTER_TYPES.some(known => known === type)

/** A subject's last encounter from the API, or null when its type is not one the workspace shows. */
export function toLatestEncounter(
  encounter: { type: string; at: string } | null
): TextCheckHit['subject']['latestEncounter'] {
  return encounter && isEncounterType(encounter.type)
    ? { type: encounter.type, at: encounter.at }
    : null
}

/** One hit; the whole display name goes in `givenName`, the short id in `subjectNo`. */
export function toTextCheckHit(dto: TextCheckResultDto): TextCheckHit {
  return {
    subject: {
      id: dto.subject_ref,
      subjectNo: dto.short_id,
      captureTag: dto.latest_capture_tag,
      givenName: dto.display_name,
      surname: null,
      nickname: null,
      nameNative: dto.name_native,
      sex: 'unknown',
      portraitUrl: dto.portrait_url,
      latestEncounter: toLatestEncounter(dto.last_encounter),
      enrolledAt: dto.enrolled_at,
    },
    field: MATCH_FIELD[dto.matched_field] ?? 'name',
    matchKind: MATCH_KIND[dto.match_kind] ?? 'exact',
    matchedText: null,
    holder: dto.holder,
    latestEventRef: dto.latest_event_ref ?? null,
  }
}

/** An answer: its hits (maybe none), or not checked with the server's reason. */
export function toTextCheckResult(dto: TextCheckResponse, caseName: string): TextCheckResult {
  if (dto.status === 'not_checked') {
    return {
      status: 'not_checked',
      reason: dto.reason === 'index_not_ready' ? 'index_not_ready' : 'error',
    }
  }
  return {
    status: 'done',
    hits: dto.results.map(toTextCheckHit),
    scope: { caseName, checkedAt: dto.searched_at, audited: true },
  }
}

export type TextCheckFailure =
  | { kind: 'not_member' }
  | { kind: 'extension_off' }
  | { kind: 'not_checked'; reason: NotCheckedReason }

const notChecked = (reason: NotCheckedReason): TextCheckFailure => ({ kind: 'not_checked', reason })

/** A failed search: a page notice (not a member, extension off), else not checked. */
export function classifyTextCheckError(error: unknown, online: boolean): TextCheckFailure {
  if (error instanceof UploadTimeoutError) return notChecked('timeout')
  if (isNotMember(error)) return { kind: 'not_member' }
  if (isExtensionOff(error)) return { kind: 'extension_off' }
  if (humintErrorCode(error) === 'audit_unavailable') return notChecked('audit_unavailable')
  if (error instanceof ApiError) return notChecked('error')
  if (isNoAnswer(error)) return notChecked(online ? 'error' : 'offline')
  return notChecked('error')
}

/** Where a search stands, as react-query reports it. */
export type TextCheckOutcome =
  | { status: 'idle' }
  | { status: 'pending' }
  | { status: 'success'; data: TextCheckResponse }
  | { status: 'error'; error: unknown }

export interface TextCheckView {
  result: TextCheckResult
  /** 403: the page shows the not-a-member notice instead. */
  notMember: boolean
}

/** The screen's view of a search. An extension switched off reads as not checked. */
export function toTextCheckView(
  outcome: TextCheckOutcome,
  context: { caseName: string; online: boolean }
): TextCheckView {
  switch (outcome.status) {
    case 'idle':
      return { result: { status: 'idle' }, notMember: false }
    case 'pending':
      return { result: { status: 'searching' }, notMember: false }
    case 'success':
      return { result: toTextCheckResult(outcome.data, context.caseName), notMember: false }
    case 'error': {
      const failure = classifyTextCheckError(outcome.error, context.online)
      if (failure.kind === 'not_member') return { result: { status: 'idle' }, notMember: true }
      const reason = failure.kind === 'not_checked' ? failure.reason : 'error'
      return { result: { status: 'not_checked', reason }, notMember: false }
    }
  }
}
